import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { RestaurantService } from '../../../../services/restaurant.service';
import { ReservationService } from '../../../../services/reservation.service';
import { DEFAULT_RESTAURANT_TIME_ZONE } from '../../../../services/reservation-time.util';
import {
  CLOSURE_PRESETS,
  ClosurePreset,
  HoursStatus,
  StatusTone,
  availablePresets,
  bookingsInWindow,
  canManageClosure,
  closureWindow,
  presetUntil,
  statusLine,
  whenLabel,
} from '../../../../utils/open-status.util';
import { getLoggedInUserRole } from '../../../../utils/user-role.util';

/**
 * Restaurant Status (FNF-1612): open or closed right now and why, plus "Close
 * temporarily" and "Reopen now". A closure closes the whole restaurant (orders,
 * bookings and walk-ins), so this sits with the opening hours, outside their
 * form: it stays usable for a Square or Clover restaurant, whose weekly hours
 * come from the POS.
 */
@Component({
  selector: 'app-restaurant-status',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './restaurant-status.component.html',
  styleUrl: './restaurant-status.component.css',
})
export class RestaurantStatusComponent implements OnInit {
  private readonly restaurantService = inject(RestaurantService);
  private readonly reservationService = inject(ReservationService);
  private readonly toastr = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  status: HoursStatus | null = null;
  loading = true;
  saving = false;
  /** Admins and managers close and reopen; everyone else only sees it. */
  readonly canManage = canManageClosure(getLoggedInUserRole());

  closing = false;
  preset: ClosurePreset = 'hour';
  reason = '';
  readonly maxReason = 60;
  /**
   * Bookings already inside the closure: it doesn't cancel them, so the
   * host should know before closing. null while unknown, or when the
   * restaurant takes no bookings.
   */
  bookingsInClosure: { count: number; firstDate: string | null } | null = null;
  private bookingsCheck = 0;

  ngOnInit(): void {
    this.restaurantService.hoursStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        this.status = status;
        this.keepOfferedPreset();
      });
    this.restaurantService
      .refreshHoursStatus()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe();
  }

  get timeZone(): string {
    return (
      this.restaurantService.currentRestaurantDetails$.value?.time_zone ||
      DEFAULT_RESTAURANT_TIME_ZONE
    );
  }

  get line(): { tone: StatusTone; text: string } | null {
    return this.status ? statusLine(this.status, this.timeZone) : null;
  }

  get closedNow(): boolean {
    return !!this.status?.temporary_closure;
  }

  get presets(): { value: ClosurePreset; label: string }[] {
    const offered = availablePresets(this.status);
    return CLOSURE_PRESETS.filter((p) => offered.includes(p.value));
  }

  /** What the chosen preset means, e.g. "Closed until 6:00 PM." */
  get presetHint(): string {
    const until = presetUntil(this.preset, this.status, this.timeZone);
    return until
      ? `Closed until ${whenLabel(until, this.timeZone)}.`
      : 'Closed until someone reopens it here.';
  }

  openClose(): void {
    this.reason = '';
    this.closing = true;
    this.setPreset('hour');
  }

  setPreset(preset: ClosurePreset): void {
    this.preset = preset;
    this.checkBookings();
  }

  /**
   * A refresh can take a preset off the list while the form is open (closing
   * today's date makes the restaurant not open, so "Until next opening" goes).
   * Keeping it would send `until: null` and close until reopened, so fall
   * back to the default.
   */
  private keepOfferedPreset(): void {
    if (this.closing && !availablePresets(this.status).includes(this.preset)) {
      this.setPreset('hour');
    }
  }

  /** Counts the live bookings the chosen closure would cover. */
  private checkBookings(): void {
    const check = ++this.bookingsCheck;
    this.bookingsInClosure = null;
    const until = presetUntil(this.preset, this.status, this.timeZone);
    const { from, to, dates } = closureWindow(until, this.timeZone);
    if (!dates.length) {
      return;
    }
    forkJoin(dates.map((date) => this.reservationService.listByDate(date)))
      .pipe(catchError(() => of(null)))
      .subscribe((days) => {
        if (days && check === this.bookingsCheck) {
          this.bookingsInClosure = bookingsInWindow(days.flat(), from, to);
        }
      });
  }

  cancelClose(): void {
    this.closing = false;
  }

  confirmClose(): void {
    if (this.saving) {
      return;
    }
    const until = presetUntil(this.preset, this.status, this.timeZone);
    // Belt and braces for keepOfferedPreset: "Until next opening" with no
    // opening to end must never go out as an open-ended closure.
    if (this.preset === 'next_opening' && !until) {
      this.setPreset('hour');
      this.toastr.error(
        "The restaurant isn't open any more, so choose how long to close it again.",
        'Error',
      );
      return;
    }
    this.saving = true;
    this.restaurantService
      .setTemporaryClosure({ until, reason: this.reason.trim() || null })
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.closing = false;
          this.toastr.success(
            'No orders, bookings or walk-ins until it reopens.',
            'Restaurant closed',
          );
        },
        error: (err) => this.toastr.error(this.errorMessage(err), 'Error'),
      });
  }

  reopen(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.restaurantService
      .clearTemporaryClosure()
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => this.toastr.success('Guests can order and book again.', 'Restaurant reopened'),
        error: (err) => this.toastr.error(this.errorMessage(err), 'Error'),
      });
  }

  private errorMessage(err: any): string {
    const detail = err?.error?.error?.message ?? err?.error?.detail ?? err?.message;
    return typeof detail === 'string' && detail
      ? detail
      : 'Unable to complete your action. Please try again later.';
  }
}
